import mixpanelService from "./mixpanelService";
import mixpanelEvents, { getScreenName } from "../../config/mixpanelEvents";
import { scrubPii } from "./piiScrubber";

// Raises error_triggered for every API failure. Hooked into the api layer so a
// screen without its own error handler is still covered.

export interface IApiErrorMeta {
  url: string;
  serviceName?: string;
  httpStatus?: number;
  response?: any;
  errorType?: "api" | "network";
  message?: string;
}

interface IExtractedError {
  errorCode: string;
  errorMessage: string;
  statusCode: string;
}

// Mirrors serviceMapper.errorMapper so the text matches what a screen would
// send for the same response, letting the duplicate guard match on it.
function joinErrors(errors: any[], key: string): string {
  let joined = "";
  for (let i = 0; i < errors.length; i++) {
    const text = String(errors[i]?.[key] ?? "");
    if (i === 0) joined = text;
    else if (text && !joined.includes(text)) joined += ", " + text;
  }
  return joined;
}

function firstString(...values: any[]): string {
  for (let i = 0; i < values.length; i++) {
    const value = values[i];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function extractError(response: any): IExtractedError | null {
  if (!response || typeof response !== "object") return null;

  const requestResult = response.RequestResult;
  if (requestResult) {
    const statusCode = String(requestResult.Status?.StatusCode ?? "");
    const errors: any[] = Array.isArray(requestResult.Errors)
      ? requestResult.Errors
      : [];
    const errorCode = joinErrors(errors, "ErrorCode");
    const errorMessage = joinErrors(errors, "ErrorMessage");

    if (statusCode === "1") {
      const contentError = firstString(
        response.ContentData?.error,
        response.ContentData?.Error,
        response.ContentData?.errorMessage,
        response.ContentData?.ErrorMessage
      );
      if (!errorCode && !errorMessage && !contentError) return null;
      return {
        statusCode,
        errorCode,
        errorMessage: errorMessage || contentError,
      };
    }

    return { statusCode, errorCode, errorMessage };
  }

  // mapped result: { status: false, error }
  if (response.status === false || response.error) {
    return {
      statusCode: response.status === false ? "0" : "",
      errorCode: String(response.errorCode || ""),
      errorMessage: String(response.error || ""),
    };
  }

  const flatMessage = firstString(
    response.errorMessage,
    response.ErrorMessage,
    response.error_message,
    response.Error,
    response.errorDescription
  );
  if (flatMessage) {
    return {
      statusCode: String(response.statusCode ?? response.StatusCode ?? ""),
      errorCode: firstString(response.errorCode, response.ErrorCode),
      errorMessage: flatMessage,
    };
  }

  return null;
}

export function trackApiError(meta: IApiErrorMeta) {
  try {
    const isNetwork = meta.errorType === "network";
    const extracted = extractError(meta.response);
    const httpFailed =
      meta.httpStatus !== undefined &&
      (meta.httpStatus < 200 || meta.httpStatus >= 300);

    if (!isNetwork && !extracted && !httpFailed) return;

    const error_message = scrubPii(extracted?.errorMessage || meta.message);

    mixpanelService({
      event: mixpanelEvents.ERROR_TRIGGERED,
      screen_name: getScreenName(window.location.pathname),
      api_name: meta.serviceName || "",
      api_endpoint: String(meta.url || "").split("?")[0],
      error_type: isNetwork ? "network" : "api",
      error_code: extracted?.errorCode || "",
      status_code: extracted?.statusCode || "",
      http_status: meta.httpStatus,
      error_message,
      error_object: error_message, // kept for existing dashboards
    });
  } catch (e) {
    console.error("mixpanel error_triggered failed", e);
  }
}
